import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "eqbx_pwa_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  // iPad on iOS 13+ pretends to be Mac — also check touch
  const ua = navigator.userAgent || "";
  const iPadLike = /Mac/.test(ua) && "ontouchend" in document;
  return /iPhone|iPad|iPod/.test(ua) || iPadLike;
}

function isIOSNonSafari() {
  // In-app browsers & other iOS browsers can't reliably "Add to Home Screen".
  const ua = navigator.userAgent || "";
  return /CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line\/|Twitter/i.test(ua);
}

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS legacy
    window.navigator.standalone === true
  );
}

function wasDismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const t = parseInt(v, 10);
    return Number.isFinite(t) && Date.now() - t < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [iosSteps, setIosSteps] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setShow(false);
      setIosHint(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show a hint once after a delay.
    let iosTimer = null;
    if (isIOS() && !isStandalone()) {
      iosTimer = setTimeout(() => setIosHint(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
    setShow(false);
    setIosHint(false);
    setIosSteps(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user dismissed the native prompt */
    }
    setDeferred(null);
    setShow(false);
  };

  if (show) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="md:hidden fixed left-3 right-3 z-50 rounded-lg border flex items-center gap-3 px-4 py-3"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0) + 72px)",
          background: "var(--bg-surface)",
          borderColor: "var(--border-soft)",
          color: "var(--text-primary)",
          boxShadow: "0 1px 2px rgba(15,28,40,0.06)",
        }}
      >
        <div
          className="w-9 h-9 rounded flex items-center justify-center shrink-0"
          style={{ background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }}
        >
          <Download className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 leading-tight">
          <p className="text-sm font-medium">Install EquityBox</p>
          <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Add to home screen for quick access</p>
        </div>
        <button
          data-testid="pwa-install-button"
          onClick={install}
          className="text-xs font-medium px-3 py-1.5 rounded"
          style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}
        >
          Install
        </button>
        <button
          data-testid="pwa-dismiss-button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="p-1 opacity-70 hover:opacity-100"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    );
  }

  if (iosSteps) {
    const inAppBrowser = isIOSNonSafari();
    return (
      <div
        data-testid="pwa-ios-steps"
        className="md:hidden fixed inset-0 z-[60] flex items-end"
        style={{ background: "rgba(0,0,0,0.45)" }}
        onClick={() => setIosSteps(false)}
      >
        <div
          className="w-full rounded-t-lg px-5 pt-5 pb-8"
          style={{
            background: "var(--bg-surface)",
            color: "var(--text-primary)",
            borderTop: "1px solid var(--border-soft)",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 24px)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <p className="text-base font-semibold">Add EquityBox to Home Screen</p>
            <button
              data-testid="pwa-ios-steps-close"
              onClick={dismiss}
              aria-label="Close"
              className="p-1 opacity-70"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
          {inAppBrowser ? (
            <div className="space-y-3 text-sm leading-relaxed">
              <p>
                This browser can&apos;t add apps to your Home Screen. Please open this
                site in <span className="font-semibold">Safari</span> first:
              </p>
              <ol className="list-decimal ml-5 space-y-2">
                <li>Copy this page&apos;s link.</li>
                <li>Open the <span className="font-semibold">Safari</span> app and paste the link.</li>
                <li>
                  Tap the <Share className="inline w-4 h-4 mb-0.5" strokeWidth={2} />{" "}
                  <span className="font-semibold">Share</span> button, then choose{" "}
                  <span className="font-semibold">&ldquo;Add to Home Screen&rdquo;</span>.
                </li>
              </ol>
            </div>
          ) : (
            <ol className="list-decimal ml-5 space-y-3 text-sm leading-relaxed">
              <li>
                Tap the <Share className="inline w-4 h-4 mb-0.5" strokeWidth={2} />{" "}
                <span className="font-semibold">Share</span> button in Safari&apos;s toolbar
                (bottom of the screen).
              </li>
              <li>
                Scroll down the menu and tap{" "}
                <span className="font-semibold">&ldquo;Add to Home Screen&rdquo;</span>.
              </li>
              <li>
                Tap <span className="font-semibold">&ldquo;Add&rdquo;</span> in the top-right corner.
              </li>
            </ol>
          )}
          <p className="text-xs opacity-60 mt-4">
            Tip: if the icon doesn&apos;t appear right away, swipe to your last Home
            Screen page or check the App Library — iOS sometimes places it there.
          </p>
        </div>
      </div>
    );
  }

  if (iosHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="md:hidden fixed left-3 right-3 z-50 rounded-lg border px-4 py-3"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0) + 72px)",
          background: "var(--bg-surface)",
          borderColor: "var(--border-soft)",
          color: "var(--text-primary)",
          boxShadow: "0 1px 2px rgba(15,28,40,0.06)",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded flex items-center justify-center shrink-0"
            style={{ background: "var(--accent-primary)", color: "var(--btn-primary-fg)" }}
          >
            <Share className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <button
            data-testid="pwa-ios-hint-open"
            onClick={() => setIosSteps(true)}
            className="flex-1 leading-snug text-left"
          >
            <p className="text-sm font-medium">Add EquityBox to Home Screen</p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
              Tap here to see how — it takes 10 seconds.
            </p>
          </button>
          <button
            data-testid="pwa-ios-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="p-1 opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
